import React, { useEffect, useState } from "react";
import { useDatabase, useToast, Toaster } from "@/lib";
import { tabsConfig } from "@/config";

/* core-tab components */
import Catalog           from "@/components/catalog/Catalog";
import TableRegistration from "@/components/core/TableRegistration";
import SchemaViewer      from "@/components/core/SchemaViewer";
import TableDataExplorer from "@/components/core/TableDataExplorer";
import FilterDownload    from "@/components/core/FilterDownload";
import AssetGenerator    from "@/components/catalog/AssetGenerator";
import QueryEditor       from "@/components/core/QueryEditor";

/* UI / icons */
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import {
  Database, Layout, Table, FileSymlink,
  Download as DownloadIcon, RefreshCw, Code, X,
} from "lucide-react";

interface QueryTab { id: string; label: string; query: string; }

export default function App() {
  const { conn, loading, error, tables, refreshTables } = useDatabase();
  const { toast } = useToast();
  const firstTabKey = tabsConfig[0]?.key ?? "catalog";

  const [activeTab, setActiveTab] = useState(firstTabKey);
  const [queryTabs, setQueryTabs] = useState<QueryTab[]>([
    { id: "query-1", label: "Query 1", query: "" },
  ]);

  /* status messages */
  const setMessage = (msg: string) => {
    if (!/^Loading|^Running query/.test(msg)) toast({ title: msg });
  };

  /* keep table list current */
  useEffect(() => { if (conn) refreshTables(); }, [conn, refreshTables]);

  /* ─────────── loading / error guards ─────────── */
  if (loading) return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="h-16 w-16 mx-auto mb-4 border-4 border-t-brand rounded-full animate-spin" />
        <h2 className="text-2xl font-semibold">Loading DuckDB…</h2>
        <p className="text-muted-foreground">Initializing in-memory database</p>
      </div>
    </div>
  );
  if (error) return (
    <div className="flex h-screen items-center justify-center">
      <div className="max-w-md p-6 border rounded-lg shadow-lg bg-destructive/5">
        <h2 className="text-2xl font-bold text-destructive mb-4">Error Initializing DuckDB</h2>
        <p className="text-destructive mb-4">{error.message}</p>
        <p className="text-sm text-muted-foreground">This could be a browser / WASM issue.</p>
      </div>
    </div>
  );

  /* ─────────── main layout ─────────── */
  return (
    <div className="flex min-h-screen bg-background">
      {/* ───── sidebar ───── */}
      <aside className="w-64 border-r bg-muted/30">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Database className="h-5 w-5 text-brand" /> DuckDB Explorer
          </h1>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Database Tables</h2>
            <Button variant="ghost" size="sm" onClick={refreshTables}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <ScrollArea className="h-[calc(100vh-220px)]">
            {tables.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2 px-1">No tables registered</div>
            ) : (
              <ul className="space-y-1">
                {tables.map(tbl => (
                  <li key={tbl}
                      className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted cursor-pointer"
                      onDoubleClick={() => {
                        if (activeTab.startsWith("query-")) {
                          setQueryTabs(prev => prev.map(t => t.id === activeTab
                            ? { ...t, query: t.query ? `${t.query} ${tbl}` : `SELECT * FROM ${tbl} LIMIT 100` }
                            : t));
                        } else {
                          const newId = `query-${queryTabs.length + 1}`;
                          setQueryTabs([...queryTabs, {
                            id: newId, label: `Query ${queryTabs.length + 1}`,
                            query: `SELECT * FROM ${tbl} LIMIT 100`,
                          }]);
                          setActiveTab(newId);
                        }
                      }}>
                    <Table className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{tbl}</span>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </div>
      </aside>

      {/* ───── main content ───── */}
      <main className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          {/* tab bar */}
          <div className="border-b px-4">
            <TabsList className="mt-2">
              {tabsConfig.map(tab => {
                const IconMap = { Database, Layout, Table, FileSymlink, DownloadIcon, RefreshCw, Code, X };
                const Icon = IconMap[tab.icon as keyof typeof IconMap] || Database;
                return (
                  <TabsTrigger key={tab.key} value={tab.key} className="gap-1.5">
                    <Icon className="h-4 w-4" /> {tab.label}
                  </TabsTrigger>
                );
              })}

              {/* dynamic SQL tabs */}
              {queryTabs.map(tab => (
                <TabsTrigger key={tab.id} value={tab.id} asChild>
                  <div className="group relative pr-7 inline-flex items-center gap-1.5">
                    <Code className="h-4 w-4" />
                    <span>{tab.label}</span>
                    <button onClick={e => {
                              e.preventDefault(); e.stopPropagation();
                              const remaining = queryTabs.filter(t => t.id !== tab.id);
                              setQueryTabs(remaining);
                              if (activeTab === tab.id) setActiveTab(remaining[0]?.id ?? firstTabKey);
                            }}
                            className="absolute right-1 opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-muted">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </TabsTrigger>
              ))}

              {/* “+” new-query button */}
              <span role="button" tabIndex={0}
                    onClick={() => {
                      const newId = `query-${queryTabs.length + 1}`;
                      setQueryTabs([...queryTabs, { id: newId, label: `Query ${queryTabs.length + 1}`, query: "" }]);
                      setActiveTab(newId);
                    }}
                    className="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm hover:bg-muted/50 ml-2 cursor-pointer select-none">
                +
              </span>
            </TabsList>
          </div>

          {/* tab contents */}
          <div className="flex-1 overflow-auto p-6">
            {tabsConfig.map(tab => (
              <TabsContent key={tab.key} value={tab.key} className="h-full">
                {(() => {
                  switch (tab.key) {
                    case "catalog":  return <Catalog conn={conn} onTableRegistered={refreshTables} />;
                    case "register": return <TableRegistration conn={conn} onRefresh={refreshTables} onSchemaLoad={refreshTables} setMsg={setMessage} />;
                    case "schema":   return <SchemaViewer initialTable={tables[0]} setMsg={setMessage} />;
                    case "data":     return <TableDataExplorer conn={conn} tables={tables} setMsg={setMessage} />;
                    case "filter":   return <FilterDownload conn={conn} tables={tables} setMsg={setMessage} />;
                    case "generator":return <AssetGenerator conn={conn} />;
                    default:         return null;
                  }
                })()}
              </TabsContent>
            ))}

            {/* SQL editors */}
            {queryTabs.map(tab => (
              <TabsContent key={tab.id} value={tab.id} className="h-full">
                <QueryEditor tabId={tab.id} initialQuery={tab.query}
                             onQueryChange={q => setQueryTabs(prev => prev.map(t => t.id === tab.id ? { ...t, query: q } : t))}
                             setMsg={setMessage} />
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </main>
      <Toaster />
    </div>
  );
}
